import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import SingleEvent from '../../classes/events/SingleEvent';
import { resolveAddress } from '../../utils/geocodeEdge';
import AddressAutocomplete from './AddressAutocomplete';
import BannerCropper from './BannerCropper';
import imageCompression from 'browser-image-compression';
import {
  clampMaxParticipantsToTier,
  isAllowedMaxParticipants,
  userCanUseExtendedMaxParticipantsCap,
} from '../../utils/eventParticipantLimits';
import { isEventNotFinishedYet } from '../../utils/eventSchedule';
import MaxParticipantsSelect from './MaxParticipantsSelect';
import './CreateEventForm.css';

const TIME_INPUT_STEP_SECONDS = 5 * 60;

function formatTimeForInput(time) {
  if (!time) return '';
  const s = String(time);
  if (s.length === 5 && s.includes(':')) {
    return s;
  }
  if (s.length >= 5) {
    return s.substring(0, 5);
  }
  return s;
}

function roundTimeToFiveMinutes(time) {
  const normalized = formatTimeForInput(time);
  const match = /^(\d{2}):(\d{2})$/.exec(normalized);
  if (!match) return normalized;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return normalized;

  const total = hours * 60 + minutes;
  const rounded = Math.round(total / 5) * 5;
  const capped = Math.min(rounded, (24 * 60) - 5);
  const nextHours = Math.floor(capped / 60);
  const nextMinutes = capped % 60;
  return `${String(nextHours).padStart(2, '0')}:${String(nextMinutes).padStart(2, '0')}`;
}

function normalizeTime(time) {
  if (!time) return '';
  const s = String(time);
  if (s.length >= 5) {
    return s.substring(0, 5);
  }
  return s;
}

function CreateEventForm({ onClose, onSuccess, compact = false, event = null }) {
  const { t } = useTranslation('events');
  const isEdit = Boolean(event?.id);
  const isEditingFinishedEvent = isEdit && !isEventNotFinishedYet(event);

  // Form state (edit: seed from `event` on mount)
  const [title, setTitle] = useState(() => (isEdit ? event.title || '' : ''));
  const [eventType, setEventType] = useState(() => (isEdit ? event.event_type || '' : ''));
  const [description, setDescription] = useState(() => (isEdit ? event.description || '' : ''));
  const [eventDate, setEventDate] = useState(() => (isEdit ? event.event_date || '' : ''));
  const [startTime, setStartTime] = useState(() =>
    isEdit ? roundTimeToFiveMinutes(event.start_time || '') : ''
  );
  const [durationMinutes, setDurationMinutes] = useState(() =>
    isEdit ? event.duration_minutes ?? 120 : 120
  );
  const [location, setLocation] = useState(() => (isEdit ? event.location || '' : ''));
  const [maxParticipants, setMaxParticipants] = useState(() =>
    isEdit ? String(event.max_participants ?? '4') : '4'
  );
  const [isFree, setIsFree] = useState(() => (isEdit ? event.is_free !== false : true));
  const [totalCost, setTotalCost] = useState(() =>
    isEdit && event.total_cost != null ? String(event.total_cost) : ''
  );
  const [showAdditionalInstructions, setShowAdditionalInstructions] = useState(() =>
    isEdit ? Boolean(event.additional_instructions && String(event.additional_instructions).trim()) : false
  );
  const [additionalInstructions, setAdditionalInstructions] = useState(() =>
    isEdit ? event.additional_instructions?.trim() || '' : ''
  );

  // Banner image state
  const [bannerPreview, setBannerPreview] = useState(() =>
    isEdit && event.banner_url && !String(event.banner_url).startsWith('blob:')
      ? event.banner_url
      : null
  );
  const bannerFileRef = useRef(null);
  const [hasBannerFile, setHasBannerFile] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [cropperImageSrc, setCropperImageSrc] = useState(null);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [extendedMaxParticipants, setExtendedMaxParticipants] = useState(false);
  const [participantTierLoaded, setParticipantTierLoaded] = useState(false);

  // Character counters
  const titleLength = title.length;
  const descriptionLength = description.length;
  const additionalInstructionsLength = additionalInstructions.length;

  // Cleanup dla blob URLs przy unmount
  useEffect(() => {
    return () => {
      if (bannerPreview && typeof bannerPreview === 'string' && bannerPreview.startsWith('blob:')) {
        URL.revokeObjectURL(bannerPreview);
      }
      if (cropperImageSrc && typeof cropperImageSrc === 'string' && cropperImageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(cropperImageSrc);
      }
    };
  }, [bannerPreview, cropperImageSrc]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user || cancelled) {
          if (!cancelled) setParticipantTierLoaded(true);
          return;
        }
        const { data, error: profileError } = await supabase
          .from('users')
          .select('user_role, is_premium')
          .eq('id', user.id)
          .single();

        if (cancelled) return;

        if (profileError) {
          console.warn('CreateEventForm: could not load participant tier', profileError);
          setExtendedMaxParticipants(false);
        } else {
          setExtendedMaxParticipants(
            userCanUseExtendedMaxParticipantsCap({
              userRole: data?.user_role,
              isPremium: data?.is_premium,
            })
          );
        }
      } finally {
        if (!cancelled) setParticipantTierLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!participantTierLoaded) return;
    const clamped = clampMaxParticipantsToTier(maxParticipants, extendedMaxParticipants);
    if (clamped !== maxParticipants) setMaxParticipants(clamped);
  }, [participantTierLoaded, extendedMaxParticipants, maxParticipants]);

  // Calculate duration display
  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) {
      return `≈ ${mins}min`;
    } else if (mins === 0) {
      return `≈ ${hours}h`;
    } else {
      return `≈ ${hours}h ${mins}min`;
    }
  };

  // Validation
  const validateForm = () => {
    if (!title.trim() || titleLength > 100) {
      setError(t('events.createEvent.errors.title'));
      return false;
    }
    if (!eventType) {
      setError(t('events.createEvent.errors.eventType'));
      return false;
    }
    if (descriptionLength > 500) {
      setError(t('events.createEvent.errors.description'));
      return false;
    }
    if (!eventDate) {
      setError(t('events.createEvent.errors.date'));
      return false;
    }
    if (!isEdit) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(eventDate);
      if (selectedDate < today) {
        setError(t('events.createEvent.errors.datePast'));
        return false;
      }
    }
    if (!startTime) {
      setError(t('events.createEvent.errors.startTime'));
      return false;
    }
    if (!durationMinutes || durationMinutes <= 0) {
      setError(t('events.createEvent.errors.duration'));
      return false;
    }
    if (!location.trim()) {
      setError(t('events.createEvent.errors.location'));
      return false;
    }
    if (!maxParticipants) {
      setError(t('events.createEvent.errors.maxParticipants'));
      return false;
    }
    const mp = parseInt(maxParticipants, 10);
    if (!isAllowedMaxParticipants(mp, extendedMaxParticipants)) {
      setError(t('events.createEvent.errors.maxParticipantsNotAllowed'));
      return false;
    }
    if (!isFree && (!totalCost || parseFloat(totalCost) <= 0)) {
      setError(t('events.createEvent.errors.totalCost', 'Podaj całkowity koszt wydarzenia'));
      return false;
    }
    if (showAdditionalInstructions && additionalInstructionsLength > 500) {
      setError(
        t(
          'events.createEvent.errors.additionalInstructionsTooLong',
          'Dodatkowe instrukcje mogą mieć maksymalnie 500 znaków.'
        )
      );
      return false;
    }
    return true;
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Sprawdź rozmiar pliku (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError(t('events.createEvent.errors.bannerSize', 'Obraz jest za duży. Maksymalny rozmiar to 10MB.'));
      return;
    }

    // Sprawdź typ pliku
    if (!file.type.startsWith('image/')) {
      setError(t('events.createEvent.errors.bannerInvalidType', 'Nieprawidłowy typ pliku. Wybierz obraz.'));
      return;
    }

    // Reset input value aby można było wybrać ten sam plik ponownie
    e.target.value = '';

    // Utwórz URL do wyświetlenia w cropperze
    const imageUrl = URL.createObjectURL(file);
    setCropperImageSrc(imageUrl);
    setShowCropper(true);
    setError(null);
  };

  const handleCropComplete = (croppedFile) => {
    // Revoke poprzedni blob URL jeśli istnieje
    if (bannerPreview && typeof bannerPreview === 'string' && bannerPreview.startsWith('blob:')) {
      URL.revokeObjectURL(bannerPreview);
    }
    if (cropperImageSrc) {
      URL.revokeObjectURL(cropperImageSrc);
    }

    // Ustaw przycięty plik
    bannerFileRef.current = croppedFile;
    setHasBannerFile(true);

    // Utwórz preview z przyciętego pliku
    const previewUrl = URL.createObjectURL(croppedFile);
    setBannerPreview(previewUrl);

    // Zamknij cropper
    setShowCropper(false);
    setCropperImageSrc(null);
  };

  const handleCropCancel = () => {
    // Revoke blob URL jeśli istnieje
    if (cropperImageSrc) {
      URL.revokeObjectURL(cropperImageSrc);
    }
    setShowCropper(false);
    setCropperImageSrc(null);
  };

  const handleRemoveBanner = () => {
    // Revoke blob URL jeśli istnieje
    if (bannerPreview && typeof bannerPreview === 'string' && bannerPreview.startsWith('blob:')) {
      URL.revokeObjectURL(bannerPreview);
    }
    setBannerPreview(null);
    bannerFileRef.current = null;
    setHasBannerFile(false);
  };

  const uploadBanner = async (eventId, file) => {
    if (!file) return null;

    try {
      setUploadingBanner(true);
      
      // Sprawdź czy to prawdziwy File object
      if (!(file instanceof File) && !(file instanceof Blob)) {
        throw new Error('Invalid file object');
      }
      
      // Kompresuj obraz przed uploadem
      const options = {
        maxSizeMB: 1, // Maksymalny rozmiar pliku po kompresji
        maxWidthOrHeight: 1920, // Maksymalna szerokość/wysokość (16:9 = 1920x1080)
        useWebWorker: true,
        fileType: file.type || 'image/jpeg'
      };
      
      let compressed;
      try {
        compressed = await imageCompression(file, options);
      } catch (compressionError) {
        console.error('Error compressing image:', compressionError);
        throw new Error('Failed to process image. Please try a different image.');
      }

      // Przygotuj ścieżkę
      const fileExt = file.name.split('.').pop().toLowerCase() || 'jpg';
      const timestamp = Date.now();
      const filePath = `${eventId}/${timestamp}.${fileExt}`;
      const contentType = compressed.type || `image/${fileExt}`;

      // Konwertuj na ArrayBuffer
      const arrayBuffer = await compressed.arrayBuffer();

      // Upload do Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('event-banners')
        .upload(filePath, arrayBuffer, {
          cacheControl: '3600',
          upsert: false,
          contentType: contentType,
        });

      if (uploadError) {
        console.error('Upload error details:', uploadError);
        
        if (uploadError.message?.includes('mime type') || uploadError.message?.includes('not supported')) {
          throw new Error('Invalid file type. Please use JPG, PNG, GIF, or WebP format.');
        } else if (uploadError.message?.includes('Bucket not found')) {
          throw new Error('Banner storage bucket not found. Please contact administrator.');
        } else if (uploadError.message?.includes('new row violates row-level security')) {
          throw new Error('Permission denied. You may not have permission to upload banners.');
        }
        
        throw uploadError;
      }

      // Pobierz publiczny URL
      const { data: { publicUrl } } = supabase.storage
        .from('event-banners')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (err) {
      console.error('Error uploading banner:', err);
      throw err;
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (isEditingFinishedEvent) {
      setError(
        t(
          'events.editEvent.errors.finishedEvent',
          'Zakończonego wydarzenia nie można już edytować ani przenosić z historii do aktywnych.'
        )
      );
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError) throw userError;
      if (!user) {
        throw new Error(
          isEdit ? t('events.editEvent.errors.notLoggedIn') : t('events.createEvent.errors.notLoggedIn')
        );
      }

      if (isEdit) {
        if (event.host_id !== user.id) {
          throw new Error(t('events.editEvent.errors.notHost'));
        }
        if (isEditingFinishedEvent) {
          throw new Error(
            t(
              'events.editEvent.errors.finishedEvent',
              'Zakończonego wydarzenia nie można już edytować ani przenosić z historii do aktywnych.'
            )
          );
        }

        const changes = [];
        const oldEvent = event;
        const oldTimeNormalized = normalizeTime(oldEvent.start_time);
        const newTimeNormalized = normalizeTime(startTime);

        if (oldEvent.title !== title.trim()) {
          changes.push('title');
        }
        if ((oldEvent.event_type || '') !== eventType) {
          changes.push('eventType');
        }
        if (oldEvent.description !== (description.trim() || null)) {
          changes.push('description');
        }
        if (oldEvent.event_date !== eventDate) {
          changes.push('date');
        }
        if (oldTimeNormalized !== newTimeNormalized) {
          changes.push('startTime');
        }
        if (oldEvent.duration_minutes !== parseInt(durationMinutes, 10)) {
          changes.push('duration');
        }
        if (oldEvent.max_participants !== parseInt(maxParticipants, 10)) {
          changes.push('maxParticipants');
        }
        if (oldEvent.is_free !== isFree) {
          changes.push('isFree');
        }
        if ((oldEvent.total_cost || null) !== (!isFree && totalCost ? parseFloat(totalCost) : null)) {
          changes.push('totalCost');
        }

        const oldLoc = (oldEvent.location || '').trim();
        const newLoc = location.trim();
        if (oldLoc !== newLoc) {
          changes.push('location');
        }

        const oldBanner = oldEvent.banner_url || null;
        const willHaveNewBanner = hasBannerFile && bannerFileRef.current;
        const bannerRemoved = Boolean(oldBanner && !willHaveNewBanner && !bannerPreview);
        const bannerChanged = Boolean(willHaveNewBanner || bannerRemoved);
        if (bannerChanged) {
          changes.push('banner');
        }

        const newAdditionalStored =
          showAdditionalInstructions && additionalInstructions.trim()
            ? additionalInstructions.trim()
            : null;
        const oldAdditionalStored = oldEvent.additional_instructions?.trim() || null;
        if (oldAdditionalStored !== newAdditionalStored) {
          changes.push('additionalInstructions');
        }

        if (changes.length === 0) {
          if (onClose) {
            onClose();
          }
          return;
        }

        const updateData = {
          title: title.trim(),
          event_type: eventType,
          description: description.trim() || null,
          event_date: eventDate,
          start_time: startTime,
          duration_minutes: parseInt(durationMinutes, 10),
          max_participants: parseInt(maxParticipants, 10),
          is_free: isFree,
          total_cost: !isFree && totalCost ? parseFloat(totalCost) : null,
          additional_instructions: newAdditionalStored,
        };

        if (changes.includes('location')) {
          const geo = await resolveAddress(newLoc);
          if (!geo || geo.lat == null || geo.lng == null) {
            setError(t('events.editEvent.errors.geocodeFailed'));
            return;
          }
          updateData.location = newLoc;
          updateData.latitude = geo.lat;
          updateData.longitude = geo.lng;
        }

        if (bannerChanged) {
          if (willHaveNewBanner) {
            try {
              updateData.banner_url = await uploadBanner(event.id, bannerFileRef.current);
            } catch (uploadErr) {
              console.error('Banner upload failed:', uploadErr);
              setError(t('events.editEvent.errors.bannerUpload'));
              return;
            }
          } else {
            updateData.banner_url = null;
          }
        }

        const updateResult = await SingleEvent.updateEvent(event.id, user.id, updateData);

        if (!updateResult.success || updateResult.error) {
          const err = updateResult.error || new Error(t('events.editEvent.errors.general'));
          const isEventTypeConstraintError =
            err.code === '23514' &&
            (err.message?.includes('events_event_type_check') ||
              err.details?.includes('events_event_type_check'));
          if (isEventTypeConstraintError) {
            throw new Error(
              t(
                'events.createEvent.errors.eventTypeConstraint',
                'Baza danych nie akceptuje obecnych typów wydarzeń. Uruchom migrację: databases/045-add-gaming-event-type.sql'
              )
            );
          }
          throw err;
        }

        const systemMessage = SingleEvent.formatUpdateSystemMessage(changes, t);
        await SingleEvent.createSystemMessage(event.id, systemMessage);

        if (onSuccess) {
          onSuccess(updateResult.data || { ...event, ...updateData });
        }
        if (onClose) {
          onClose();
        }
        return;
      }

      // Upload banner if provided (before creating event)
      let bannerUrl = null;
      if (hasBannerFile && bannerFileRef.current) {
        try {
          // We need to create a temporary event ID or upload with a temp name
          // For now, we'll upload first, then create event, then update if needed
          // Actually, better approach: create event first, then upload banner
          // But we need event ID for the path... Let's upload after event creation
        } catch (uploadErr) {
          console.error('Banner upload failed:', uploadErr);
          setError(t('events.createEvent.errors.bannerUpload', 'Nie udało się przesłać banera. Spróbuj ponownie.'));
          setLoading(false);
          return;
        }
      }

      const locationTrimmed = location.trim();
      const geo = await resolveAddress(locationTrimmed);
      if (!geo || geo.lat == null || geo.lng == null) {
        setError(
          t(
            'events.createEvent.errors.geocodeFailed',
            'Nie udało się ustalić współrzędnych adresu na mapie. Sprawdź adres lub spróbuj ponownie później.'
          )
        );
        setLoading(false);
        return;
      }

      // Insert event into database (współrzędne z Edge geocode — mapa nie geokoduje w locie)
      const { data, error: insertError } = await supabase
        .from('events')
        .insert({
          host_id: user.id,
          title: title.trim(),
          event_type: eventType,
          description: description.trim() || null,
          event_date: eventDate,
          start_time: startTime,
          duration_minutes: parseInt(durationMinutes),
          location: locationTrimmed,
          latitude: geo.lat,
          longitude: geo.lng,
          max_participants: parseInt(maxParticipants),
          is_free: isFree,
          total_cost: !isFree && totalCost ? parseFloat(totalCost) : null,
          additional_instructions:
            showAdditionalInstructions && additionalInstructions.trim()
              ? additionalInstructions.trim()
              : null,
          banner_url: null, // Will be updated after upload
        })
        .select()
        .single();

      if (insertError) {
        const isEventTypeConstraintError =
          insertError.code === '23514' &&
          (insertError.message?.includes('events_event_type_check') ||
            insertError.details?.includes('events_event_type_check'));

        if (isEventTypeConstraintError) {
          throw new Error(
            t(
              'events.createEvent.errors.eventTypeConstraint',
              'Baza danych nie akceptuje obecnych typów wydarzeń. Uruchom migrację: databases/045-add-gaming-event-type.sql'
            )
          );
        }

        throw insertError;
      }

      // Upload banner after event creation (now we have event ID)
      if (hasBannerFile && bannerFileRef.current) {
        try {
          bannerUrl = await uploadBanner(data.id, bannerFileRef.current);
          
          // Update event with banner URL
          const { error: updateError } = await supabase
            .from('events')
            .update({ banner_url: bannerUrl })
            .eq('id', data.id);
          
          if (updateError) {
            console.error('Error updating event with banner URL:', updateError);
            // Don't fail the whole operation, just log the error
          } else {
            // Update data object with banner URL
            data.banner_url = bannerUrl;
          }
        } catch (uploadErr) {
          console.error('Banner upload failed:', uploadErr);
          // Don't fail the whole operation, just log the error
          // The event was created successfully, banner is optional
        }
      }

      // Automatically add the host as a participant
      // Host zawsze powinien być na liście uczestników
      const { error: participantError } = await supabase
        .from('event_participants')
        .insert({
          event_id: data.id,
          user_id: user.id,
          status: 'active',
        });

      if (participantError) {
        console.error('Error adding host as participant:', participantError);
        // Jeśli nie udało się dodać hosta jako uczestnika, traktujemy to jako błąd krytyczny
        // ponieważ host zawsze powinien być uczestnikiem swojego eventu
        throw new Error(
          participantError.message || 
          t('events.createEvent.errors.addHostAsParticipant') ||
          'Nie udało się dodać organizatora do listy uczestników'
        );
      }

      // Utwórz wiadomość systemową o utworzeniu eventu
      // (bez imienia hosta - komunikat jest dla wszystkich uczestników)
      await SingleEvent.createSystemMessage(
        data.id,
        t('events.chat.system.eventCreated')
      );

      // Wyślij powiadomienie do hosta o utworzeniu wydarzenia
      try {
        const { error: notificationError } = await supabase.rpc('create_notification', {
          p_user_id: user.id,
          p_type: 'event_created',
          p_data: {
            event_title: data.title
          },
          p_event_id: data.id,
          p_application_id: null
        });
        
        if (notificationError) {
          console.error('Error creating notification for host:', notificationError);
          // Nie przerywamy procesu - powiadomienie to tylko bonus
        }
      } catch (notificationErr) {
        console.error('Error creating notification for host:', notificationErr);
        // Nie przerywamy procesu - powiadomienie to tylko bonus
      }

      // Success
      if (onSuccess) {
        onSuccess(data);
      }
      if (onClose) {
        onClose();
      }
    } catch (err) {
      setError(
        err.message ||
          (isEdit ? t('events.editEvent.errors.general') : t('events.createEvent.errors.general'))
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={`create-event-form${compact ? ' create-event-form--compact' : ''}`}>
      {/* Title */}
      <div className="form-group">
        <label htmlFor="title">
          {t('events.createEvent.titleLabel')} *
        </label>
        <input
          id="title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={100}
          required
          disabled={loading}
          placeholder={t('events.createEvent.titlePlaceholder')}
        />
        <div className="char-counter">
          {titleLength}/100
        </div>
      </div>

      {/* Typ wydarzenia + liczba miejsc (jeden wiersz) */}
      <div className="create-event-type-places-row">
        <div className="form-group">
          <label htmlFor="eventType">
            {t('events.createEvent.eventType')} *
          </label>
          <select
            id="eventType"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            required
            disabled={loading}
          >
            <option value="">{t('events.createEvent.eventTypePlaceholder')}</option>
            <option value="sport">{t('events.createEvent.eventTypes.sport')}</option>
            <option value="party">{t('events.createEvent.eventTypes.party')}</option>
            <option value="gaming">{t('events.createEvent.eventTypes.gaming')}</option>
            <option value="hangout">{t('events.createEvent.eventTypes.hangout')}</option>
            <option value="trip">{t('events.createEvent.eventTypes.trip')}</option>
            <option value="other">{t('events.createEvent.eventTypes.other')}</option>
          </select>
        </div>
        <div className="form-group form-group-max-participants">
          <label htmlFor="maxParticipants">
            {t('events.createEvent.maxParticipants')} *
          </label>
          <MaxParticipantsSelect
            id="maxParticipants"
            value={maxParticipants}
            onChange={setMaxParticipants}
            disabled={loading}
            extendedMaxParticipants={extendedMaxParticipants}
            tierLoaded={participantTierLoaded}
            participantsLabel={t('events.createEvent.participants')}
            sectionStandardLabel={t('events.createEvent.maxParticipantsGroupStandard')}
            premiumBadgeLabel={t('events.createEvent.maxParticipantsPremiumBadge')}
            listboxAriaLabel={t('events.createEvent.maxParticipants')}
          />
        </div>
      </div>

      {/* Location */}
      <div className="form-group">
        <label htmlFor="location">
          {t('events.createEvent.location')} *
        </label>
        <AddressAutocomplete
          value={location}
          onChange={setLocation}
          disabled={loading}
          placeholder={t('events.createEvent.locationPlaceholder')}
        />
      </div>

      {/* Banner Image */}
      <div className="form-group">
        <label htmlFor="banner">
          {t('events.createEvent.banner', 'Baner promujący')} ({t('events.createEvent.optional')})
        </label>
        {bannerPreview ? (
          <div className="banner-preview-container">
            <img src={bannerPreview} alt="Banner preview" className="banner-preview" />
            <div className="banner-preview-actions">
              <button
                type="button"
                className="btn-secondary btn-small"
                onClick={handleRemoveBanner}
                disabled={loading || uploadingBanner}
              >
                {t('events.createEvent.removeBanner', 'Usuń')}
              </button>
              <label htmlFor="banner" className="btn-secondary btn-small" style={{ cursor: 'pointer', margin: 0 }}>
                {t('events.createEvent.changeBanner', 'Zmień')}
                <input
                  id="banner"
                  type="file"
                  accept="image/*"
                  onChange={handleBannerChange}
                  disabled={loading || uploadingBanner}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          </div>
        ) : (
          <div className="banner-upload-container">
            <label htmlFor="banner" className="banner-upload-label">
              <input
                id="banner"
                type="file"
                accept="image/*"
                onChange={handleBannerChange}
                disabled={loading || uploadingBanner}
                style={{ display: 'none' }}
              />
              <div className="banner-upload-placeholder">
                <span className="banner-upload-icon">📷</span>
                <span className="banner-upload-text">
                  {t('events.createEvent.selectBanner', 'Wybierz obraz banera (16:9)')}
                </span>
              </div>
            </label>
          </div>
        )}
        {uploadingBanner && (
          <div className="banner-upload-progress">
            {t('events.createEvent.uploadingBanner', 'Przesyłanie banera...')}
          </div>
        )}
      </div>

      {/* Description */}
      <div className="form-group">
        <label htmlFor="description">
          {t('events.createEvent.description')}
        </label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          disabled={loading}
          placeholder={t('events.createEvent.descriptionPlaceholder')}
          rows={compact ? 3 : 4}
        />
        <div className="char-counter">
          {descriptionLength}/500
        </div>
      </div>

      <div className="form-group form-group-checkbox">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={showAdditionalInstructions}
            onChange={(e) => {
              const on = e.target.checked;
              setShowAdditionalInstructions(on);
              if (!on) {
                setAdditionalInstructions('');
              }
            }}
            disabled={loading}
          />
          <span>{t('events.createEvent.additionalInstructionsCheckbox', 'Dodatkowe instrukcje')}</span>
        </label>
      </div>

      {showAdditionalInstructions && (
        <div className="form-group">
          <label htmlFor="additionalInstructions">
            {t('events.createEvent.additionalInstructionsLabel', 'Dodatkowe instrukcje dla uczestników')}
          </label>
          <p className="additional-instructions-visibility-note">
            {t(
              'events.createEvent.additionalInstructionsVisibilityNote',
              'Widoczne tylko dla zaakceptowanych uczestników.'
            )}
          </p>
          <textarea
            id="additionalInstructions"
            value={additionalInstructions}
            onChange={(e) => setAdditionalInstructions(e.target.value)}
            maxLength={500}
            disabled={loading}
            placeholder={t(
              'events.createEvent.additionalInstructionsPlaceholder',
              'np. kod do drzwi, piętro, co zabrać…'
            )}
            rows={compact ? 2 : 3}
          />
          <div className="char-counter">
            {additionalInstructionsLength}/500
          </div>
        </div>
      )}

      <div className={`create-event-datetime-row${compact ? ' is-compact' : ''}`}>
        {/* Date */}
        <div className="form-group">
          <label htmlFor="eventDate">
            {t('events.createEvent.date')} *
          </label>
          <input
            id="eventDate"
            type="date"
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
            required
            disabled={loading}
          />
        </div>

        {/* Start Time */}
        <div className="form-group">
          <label htmlFor="startTime">
            {t('events.createEvent.startTime')} *
          </label>
          <input
            id="startTime"
            type="time"
            step={TIME_INPUT_STEP_SECONDS}
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            onBlur={(e) => setStartTime(roundTimeToFiveMinutes(e.target.value))}
            required
            disabled={loading}
          />
        </div>

        {/* Duration */}
        <div className="form-group">
          <label htmlFor="durationMinutes">
            {t('events.createEvent.duration')} *
          </label>
          <input
            id="durationMinutes"
            type="number"
            min="1"
            value={durationMinutes}
            onChange={(e) => setDurationMinutes(parseInt(e.target.value) || 0)}
            required
            disabled={loading}
          />
          <div className="duration-display">
            {formatDuration(durationMinutes)}
          </div>
        </div>
      </div>

      {/* Is Free */}
      <div className="form-group form-group-checkbox">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={isFree}
            onChange={(e) => {
              setIsFree(e.target.checked);
              if (e.target.checked) {
                setTotalCost('');
              }
            }}
            disabled={loading}
          />
          <span>{t('events.createEvent.isFree')}</span>
        </label>
      </div>

      {/* Total Cost - only show if not free */}
      {!isFree && (
        <div className="form-group">
          <label htmlFor="totalCost">
            {t('events.createEvent.totalCost', 'Całkowity koszt')} *
          </label>
          <div className="cost-input-container">
            <input
              id="totalCost"
              type="number"
              min="0"
              step="0.01"
              value={totalCost}
              onChange={(e) => setTotalCost(e.target.value)}
              required={!isFree}
              disabled={loading}
              placeholder="0.00"
            />
            <span className="cost-currency">zł</span>
          </div>
          {totalCost && maxParticipants && parseFloat(totalCost) > 0 && (
            <div className="cost-preview">
              <span className="cost-preview-label">
                {t('events.createEvent.costPerPerson', 'Koszt na osobę')}:
              </span>
              <span className="cost-preview-value">
                {totalCost} zł / {maxParticipants} {t('events.createEvent.participants', 'osób')} = 
                <strong> {(parseFloat(totalCost) / parseInt(maxParticipants)).toFixed(2)} zł</strong>
              </span>
            </div>
          )}
        </div>
      )}

      {/* Buttons */}
      <div className="form-actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={onClose}
          disabled={loading}
        >
          {t('events.createEvent.cancel')}
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={loading}
        >
          {loading
            ? isEdit
              ? t('events.editEvent.saving')
              : t('events.createEvent.creating')
            : isEdit
              ? t('events.editEvent.save')
              : t('events.createEvent.create')}
        </button>
      </div>

      {error && (
        <div className="form-error" role="alert">
          {error}
        </div>
      )}

      {/* Banner Cropper Modal */}
      {showCropper && cropperImageSrc && (
        <BannerCropper
          imageSrc={cropperImageSrc}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
    </form>
  );
}

export default CreateEventForm;
